<script setup lang="ts">
  import { NavigationCategory, NavigationItem } from '@/shared/lib';
  import { computed, watch } from 'vue';
  import { NavigationTree } from '@/shared/ui';
  import QualityRecordsTree from './quality-records/QualityRecordsTree.vue';
  import { useNavigationTree } from '@/shared/lib';

  interface Props {
    showCheckboxes?: boolean;
    showIcons?: boolean;
    selectedCategory?: string;
    selectedModules?: NavigationItem[];
    categories?: NavigationCategory[];
    items?: NavigationItem[];
  }

  const props = withDefaults(defineProps<Props>(), {
    showCheckboxes: true,
    showIcons: true,
    selectedCategory: '',
    selectedModules: () => [],
    categories: () => [],
    items: () => [],
  });

  const emit = defineEmits<{
    'item-selected': [item: NavigationItem];
    'items-selected': [items: NavigationItem[]];
    'item-navigate': [item: NavigationItem];
  }>();

  const { reset } = useNavigationTree({ useGlobalState: true });

  const currentCategory = computed(() =>
    props.categories.find((cat) => cat.id === props.selectedCategory)
  );

  // Matched by id against the current tree, never the copy kept in localStorage, which can be stale.
  const selectedModule = computed(() => {
    if (currentCategory.value?.type !== 'module') return undefined;
    const selectedId = props.selectedModules[0]?.id;
    if (selectedId === undefined) return undefined;
    return currentCategory.value.items.find((item) => item.id === selectedId);
  });

  const navigationItems = computed(() => {
    if (props.items.length > 0) {
      return props.items;
    }

    if (!currentCategory.value) return [];

    if (selectedModule.value) {
      return selectedModule.value.children ?? [];
    }

    return currentCategory.value.items;
  });

  const navigationTitle = computed(() => {
    if (!currentCategory.value) return 'NAVEGAÇÃO';
    if (selectedModule.value) return selectedModule.value.name.toUpperCase();
    if (currentCategory.value.type === 'module') return 'MÓDULO';
    return currentCategory.value.name.toUpperCase();
  });

  const showPanel = computed(() => navigationItems.value.length > 0 || !!selectedModule.value);

  const isQualityRecordsCategory = computed(() => {
    return (
      currentCategory.value?.name?.toLowerCase().includes('registros da qualidade') ||
      currentCategory.value?.items?.some(
        (item) => item.name?.toLowerCase().includes('registros da qualidade') || item.code === 'RQ'
      )
    );
  });

  const handleItemClick = (item: NavigationItem) => {
    emit('item-selected', item);
  };

  const handleItemNavigate = (item: NavigationItem) => {
    emit('item-navigate', item);
  };

  const handleItemToggle = (item: NavigationItem) => {
    emit('item-selected', item);
  };

  const handleItemExpand = (item: NavigationItem) => {};

  watch(
    () => props.selectedCategory,
    (_newCategory, oldCategory) => {
      if (oldCategory) {
        reset();
      }
    }
  );
</script>

<template>
  <div
    v-if="showPanel"
    class="h-full overflow-auto px-4 py-6"
  >
    <h3 class="text-xs/4 py-2 text-[var(--theme-text)] uppercase text-left min-w-0">
      <button
        v-if="selectedModule"
        type="button"
        class="block max-w-full truncate rounded text-left uppercase cursor-pointer hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-text)]"
        :title="selectedModule.name"
        data-testid="navigation-module-title"
        @click="handleItemNavigate(selectedModule)"
      >
        {{ navigationTitle }}
      </button>
      <template v-else>
        {{ navigationTitle }}
      </template>
    </h3>

    <div>
      <QualityRecordsTree
        v-if="isQualityRecordsCategory"
        :items="navigationItems"
        :show-checkboxes="showCheckboxes"
        :show-icons="showIcons"
        :category-info="currentCategory"
        @item-selected="handleItemClick"
        @items-selected="(items) => items.forEach((item) => handleItemClick(item))"
        @item-navigate="handleItemNavigate"
      />

      <NavigationTree
        v-else
        :items="navigationItems"
        :show-checkboxes="showCheckboxes"
        :show-icons="showIcons"
        :category-info="currentCategory"
        @item-click="handleItemClick"
        @item-toggle="handleItemToggle"
        @item-expand="handleItemExpand"
        @item-navigate="handleItemNavigate"
      />
    </div>
  </div>
</template>
